
import React from "react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { BucketFormData } from "./BucketFormData";

interface StandardBucket {
  name: string;
  defaultAllocation: number;
}

interface BucketFormFieldsProps {
  formData: BucketFormData;
  standardBuckets: StandardBucket[];
  existingBuckets: any[];
  totalBudget: number;
  remainingBudget: number;
  onFormChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => void;
  onBucketSelect: (value: string) => void;
  onAllocationSliderChange: (value: number[]) => void;
  onBudgetChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

export const BucketFormFields: React.FC<BucketFormFieldsProps> = ({
  formData,
  standardBuckets,
  existingBuckets,
  totalBudget,
  remainingBudget,
  onFormChange,
  onBucketSelect,
  onAllocationSliderChange,
  onBudgetChange
}) => {
  return (
    <div className="grid gap-4 py-4">
      <div className="grid grid-cols-4 items-center gap-4">
        <Label htmlFor="bucketType" className="text-right">
          Bucket Type
        </Label>
        <Select value={formData.name} onValueChange={onBucketSelect}>
          <SelectTrigger className="col-span-3">
            <SelectValue placeholder="Select a category" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="placeholder" disabled>Select a category</SelectItem>
            {standardBuckets.map(bucket => (
              <SelectItem 
                key={bucket.name} 
                value={bucket.name} 
                disabled={existingBuckets.some(b => b.name === bucket.name)}
              >
                {bucket.name} ({bucket.defaultAllocation}%)
              </SelectItem>
            ))}
            <SelectItem value="custom">Custom Category...</SelectItem>
          </SelectContent>
        </Select>
      </div>
      
      {formData.name === "custom" && (
        <div className="grid grid-cols-4 items-center gap-4">
          <Label htmlFor="customName" className="text-right">
            Custom Name
          </Label>
          <Input 
            id="customName" 
            name="customName" 
            className="col-span-3" 
            value={formData.customName} 
            onChange={onFormChange} 
            placeholder="Enter custom bucket name" 
          />
        </div>
      )}
      
      <div className="grid grid-cols-4 items-center gap-4">
        <Label htmlFor="allocationPercentage" className="text-right">
          Allocation %
        </Label>
        <div className="col-span-3 flex items-center gap-4">
          <Slider 
            value={[formData.allocationPercentage]} 
            max={100} 
            step={1} 
            className="flex-1" 
            onValueChange={onAllocationSliderChange} 
          />
          <span className="w-12 text-center">{Math.round(formData.allocationPercentage)}%</span>
        </div>
      </div>
      
      <div className="grid grid-cols-4 items-center gap-4">
        <Label htmlFor="budget" className="text-right">
          Budget ($)
        </Label>
        <Input 
          id="budget" 
          name="budget" 
          type="number" 
          className="col-span-3 text-black" 
          value={formData.budget} 
          onChange={onBudgetChange} 
          placeholder="Enter budget amount" 
        />
      </div>
      
      {remainingBudget < parseInt(formData.budget || "0") && (
        <div className="bg-yellow-500/10 border border-yellow-500/30 p-3 rounded-md text-sm">
          <p className="text-yellow-500">
            Warning: This allocation exceeds your remaining budget of ${remainingBudget.toLocaleString()}
          </p>
        </div>
      )}
    </div>
  );
};
